import { FileText, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useCorpus } from '@/hooks/useApi';
import { CorpusSkeleton } from '@/components/LoadingSkeletons';
import { CorpusErrorDisplay } from '@/components/CorpusErrorDisplay';
import { CorpusItem } from './CorpusItem';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/hooks/useApi';

export function CorpusList() {
  const queryClient = useQueryClient();
  const { data: corpusResponse, isLoading, error, refetch, isRefetching } = useCorpus();

  const handleRetry = async () => {
    // Invalidate and refetch corpus data
    await queryClient.invalidateQueries({ queryKey: queryKeys.corpus });
    refetch();
  };

  // Loading state with skeleton
  if (isLoading) {
    return <CorpusSkeleton count={3} />;
  }

  // Error state with comprehensive error handling
  if (error) {
    // Check if it's a 404 error (no corpus found) - treat as empty state
    const is404 = error && typeof error === 'object' && 'statusCode' in error && (error as any).statusCode === 404;
    
    if (is404) {
      return (
        <div className="p-2">
          <Card className="border-muted/50">
            <CardContent className="p-4 text-center">
              <FileText className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm font-medium text-muted-foreground mb-1">
                No corpus files available
              </p>
              <p className="text-xs text-muted-foreground">
                Upload documents to get started with your AI agent
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }
    
    return (
      <CorpusErrorDisplay
        error={error}
        onRetry={handleRetry}
        className="p-2"
      />
    );
  }

  // Empty state - check company corpus
  const companyCorpus = corpusResponse?.company;
  
  if (!companyCorpus?.files || companyCorpus.files.length === 0) {
    return (
      <div className="p-2">
        <Card className="border-muted/50">
          <CardContent className="p-4 text-center">
            <FileText className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm font-medium text-muted-foreground mb-1">
              No corpus files available
            </p>
            <p className="text-xs text-muted-foreground">
              Files will appear here when they're added to the corpus
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Validate corpus data structure
  if (typeof companyCorpus.fileCount !== 'number' || !Array.isArray(companyCorpus.files)) {
    return (
      <div className="p-2">
        <Card className="border-destructive/20 bg-destructive/5">
          <CardContent className="p-4 text-center">
            <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-destructive" />
            <p className="text-sm font-medium text-destructive mb-2">
              Invalid Data Format
            </p>
            <p className="text-xs text-muted-foreground mb-3">
              The corpus data received is not in the expected format
            </p>
            <button
              onClick={handleRetry}
              className="text-xs text-primary hover:underline"
              disabled={isRefetching}
            >
              {isRefetching ? 'Retrying...' : 'Try Again'}
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* File count display */}
      <div className="px-2">
        <span className="text-xs text-muted-foreground">
          {companyCorpus.fileCount} file{companyCorpus.fileCount !== 1 ? 's' : ''}
        </span>
      </div>

      {/* File List with loading overlay during refetch */}
      <div className="space-y-2 px-2 relative">
        {isRefetching && (
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-10 rounded">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted border-t-primary" />
              Refreshing...
            </div>
          </div>
        )}

        {companyCorpus.files.map((file, index) => (
          <div key={`${file.displayName}-${index}`} className="group">
            <CorpusItem
              file={file}
              disabled={isRefetching}
            />
          </div>
        ))}
      </div>
    </div>
  );
}